import { BellAlertIcon, Cog6ToothIcon, EllipsisVerticalIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { ENGAGEMENT_EXPORT_PATH, ENGAGEMENT_HEATMAP_EXPORT_PATH, ENGAGEMENT_QUESTIONS_EXPORT_PATH, type EngagementExportFormat } from "#shared/engagementContract.ts";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { HeaderSlot } from "@packages/core-client/components/HeaderSlot.tsx";
import { LegacyRedirectModal } from "@packages/core-client/components/LegacyRedirectModal.tsx";
import { useEnvironmentVariable, useFeatureFlags } from "@squide/firefly";
import { useI18nextInstance } from "@squide/i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useWorkleapAiClient } from "@workleap-ai/client/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button.tsx";
import { Menu, MenuContent, MenuItem, MenuLabel } from "@/components/ui/menu.tsx";
import { useEngagementClient } from "../api/query.ts";
import { useCsvExport } from "../api/useCsvExport.ts";
import { useExcelExport } from "../api/useExcelExport.ts";
import { resolveHeatmapScoreDate, toEngagementScoreDateInstant, useEngagementFilters, useHeatmapCellMode, useHeatmapScoreDate } from "../hooks/useEngagementFilters.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { ExportCsvMenuItem, ExportExcelMenuItem, ExportMenu } from "./ExportMenu.tsx";
import { SendSurveyReminderDialog } from "./SendSurveyReminderDialog.tsx";
import { useCreateEngagementPresentation, type ExportEngagementFormat } from "./useCreateEngagementPresentation.ts";

const ANALYZE_ENGAGEMENT_SKILL_SLUG = "wov_analyze_engagement";

type ExportTab = "metrics" | "questionResults" | "heatmap";

function resolveExportTab(pathname: string): ExportTab {
    if (pathname.endsWith("/question-results")) {
        return "questionResults";
    }
    if (pathname.endsWith("/heatmap")) {
        return "heatmap";
    }

    return "metrics";
}

const EXPORT_PATH_BY_TAB: Record<ExportTab, string> = {
    metrics: ENGAGEMENT_EXPORT_PATH,
    questionResults: ENGAGEMENT_QUESTIONS_EXPORT_PATH,
    heatmap: ENGAGEMENT_HEATMAP_EXPORT_PATH
};

interface CreateCustomPresentationMenuItemProps {
    format: ExportEngagementFormat;
    onAction: (format: ExportEngagementFormat) => void;
}

function CreateCustomPresentationMenuItem({ format, onAction }: CreateCustomPresentationMenuItemProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("EngagementPageActions", { i18n: i18nextInstance });
    const label = t(`createCustomPresentation.${format}`);
    const handleAction = useCallback(() => onAction(format), [onAction, format]);

    return (
        <MenuItem onAction={handleAction} textValue={label}>
            <SparklesIcon aria-hidden="true" className="text-primary" />
            <MenuLabel>{label}</MenuLabel>
        </MenuItem>
    );
}

export function EngagementPageActions() {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("EngagementPageActions", { i18n: i18nextInstance });
    const officevibeUrl = useEnvironmentVariable("officevibeUrl");

    const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
    const handleOpenReminderDialog = useCallback(() => setIsReminderDialogOpen(true), []);

    const [isSurveySettingsModalOpen, setIsSurveySettingsModalOpen] = useState(false);
    const handleOpenSurveySettingsModal = useCallback(() => setIsSurveySettingsModalOpen(true), []);

    const aiClient = useWorkleapAiClient();
    const { scope, timeFrame } = useEngagementFilters();
    const engagementClient = useEngagementClient();
    const queryClient = useQueryClient();

    const [isAnalyzing, setIsAnalyzing] = useState(false);

    const { pathname } = useLocation();
    const exportTab = resolveExportTab(pathname);
    const exportPath = EXPORT_PATH_BY_TAB[exportTab];

    const [cellMode] = useHeatmapCellMode();
    const [scoreDateParam] = useHeatmapScoreDate();

    const exportFilters = useMemo(() => {
        if (exportTab !== "heatmap") {
            return { scope, timeFrame };
        }

        const scoreDate = resolveHeatmapScoreDate(scoreDateParam);

        return { scope, timeFrame, date: cellMode === "score" ? toEngagementScoreDateInstant(scoreDate) : undefined };
    }, [exportTab, scope, timeFrame, cellMode, scoreDateParam]);

    const isAiExportEnabled = useFeatureFlags()["engagement-report-ai-export"];

    const { exportCsv, isExporting: isCsvExporting } = useCsvExport();
    const { exportExcel, isExporting: isExcelExporting } = useExcelExport();
    const { createPresentation, isCreating: isCreatingCustomPresentation } = useCreateEngagementPresentation();
    const isExportBusy = isCsvExporting || isExcelExporting || isCreatingCustomPresentation;

    const buildExportInput = useCallback((format: EngagementExportFormat) => ({ ...exportFilters, format }), [exportFilters]);

    const handleExportCsv = useCallback(async () => {
        if (!(await exportCsv(exportPath, buildExportInput("Csv")))) {
            toast.error(t("exportFailed"));
        }
    }, [exportCsv, exportPath, buildExportInput, t]);

    const handleExportExcel = useCallback(async () => {
        if (!(await exportExcel(exportPath, buildExportInput("Excel")))) {
            toast.error(t("exportFailed"));
        }
    }, [exportExcel, exportPath, buildExportInput, t]);

    const handleAnalyze = useCallback(async () => {
        setIsAnalyzing(true);

        try {
            const data = await queryClient.ensureQueryData(createQueryOptions(engagementClient.getInsight.queryOptions({ input: { scope, timeFrame } })));
            let { promptContext } = data;
            if (!promptContext) {
                ({ promptContext } = await queryClient.fetchQuery(createQueryOptions(engagementClient.getInsight.queryOptions({ input: { scope, timeFrame, requirePromptContext: true } }))));
            }
            if (!promptContext) {
                throw new Error("engagement insight: promptContext missing after forced fetch");
            }

            aiClient.askAssistant({
                prompt: t("analyzePrompt"),
                promptFeature: ANALYZE_ENGAGEMENT_SKILL_SLUG,
                selectedSkillSlugs: [ANALYZE_ENGAGEMENT_SKILL_SLUG],
                promptContext
            });
        } catch {
            toast.error(t("error"));
        } finally {
            setIsAnalyzing(false);
        }
    }, [aiClient, engagementClient, queryClient, scope, t, timeFrame]);

    return (
        <HeaderSlot>
            <div className="flex flex-wrap items-center gap-2">
                <Button intent="primary" isPending={isAnalyzing} onPress={handleAnalyze} className="print:hidden">
                    <SparklesIcon aria-hidden="true" />
                    <span>{t("analyze")}</span>
                </Button>
                <ExportMenu isBusy={isExportBusy}>
                    <ExportCsvMenuItem onAction={handleExportCsv} label={t(`exportCsv.${exportTab}`)} />
                    <ExportExcelMenuItem onAction={handleExportExcel} label={t(`exportExcel.${exportTab}`)} />
                    {isAiExportEnabled && <CreateCustomPresentationMenuItem format="Pdf" onAction={createPresentation} />}
                    {isAiExportEnabled && <CreateCustomPresentationMenuItem format="Pptx" onAction={createPresentation} />}
                </ExportMenu>
                <Menu>
                    <Button intent="outline" size="sq-md" aria-label={t("actions")} className="[--btn-bg:var(--color-card)] print:hidden">
                        <EllipsisVerticalIcon aria-hidden="true" />
                    </Button>
                    <MenuContent placement="bottom end">
                        <MenuItem onAction={handleOpenReminderDialog} textValue={t("sendReminder")}>
                            <BellAlertIcon aria-hidden="true" />
                            <MenuLabel>{t("sendReminder")}</MenuLabel>
                        </MenuItem>
                        <MenuItem onAction={handleOpenSurveySettingsModal} textValue={t("manageSurveySettings")}>
                            <Cog6ToothIcon aria-hidden="true" />
                            <MenuLabel>{t("manageSurveySettings")}</MenuLabel>
                        </MenuItem>
                    </MenuContent>
                </Menu>
            </div>

            <SendSurveyReminderDialog isOpen={isReminderDialogOpen} onOpenChange={setIsReminderDialogOpen} scope={scope} />
            <LegacyRedirectModal
                isOpen={isSurveySettingsModalOpen}
                onOpenChange={setIsSurveySettingsModalOpen}
                eyebrow={t("surveySettingsRedirect.eyebrow")}
                title={t("surveySettingsRedirect.title")}
                body={t("surveySettingsRedirect.body")}
                cancelLabel={t("surveySettingsRedirect.cancel")}
                continueLabel={t("surveySettingsRedirect.continue")}
                continueHref={`${officevibeUrl}/portal/settings/pulse-survey`}
            />
        </HeaderSlot>
    );
}
